import { Link, useNavigate } from "react-router-dom";
import { LogOut, KeyRound, Users, Settings, TrendingUp, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useAuth, hasRole } from "../context/AuthContext";

const ROLE_LABEL = {
  super_admin: "Super admin",
  titolare: "Titolare",
  commercial: "Commerciale",
};

const ROLE_COLOR = {
  super_admin: { bg: "#FEE2E2", fg: "#DC2626" },
  titolare: { bg: "#FEF3C7", fg: "#B45309" },
  commercial: { bg: "#DBEAFE", fg: "#1D4ED8" },
};

export default function UserMenu() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  if (!user) return null;
  const roleColor = ROLE_COLOR[user.role] || {
    bg: "var(--bg-subtle)",
    fg: "var(--text-secondary)",
  };

  const doLogout = async () => {
    await logout();
    toast.success("Disconnesso");
    nav("/login", { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="h-8 inline-flex items-center gap-2 px-1.5 rounded-full transition-colors"
          style={{ color: "var(--text-primary)" }}
          data-testid="user-menu-trigger"
        >
          <span
            className="h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-semibold"
            style={{ background: roleColor.bg, color: roleColor.fg }}
          >
            {user.avatar_initials || "??"}
          </span>
          <span
            className="hidden md:inline text-[13px] font-medium"
            style={{ color: "var(--text-primary)" }}
          >
            {user.full_name}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="pb-2">
          <div
            className="text-[13px] font-semibold"
            style={{ color: "var(--text-primary)" }}
          >
            {user.full_name}
          </div>
          <div
            className="text-[11px]"
            style={{ color: "var(--text-muted)" }}
          >
            {user.email}
          </div>
          <span
            className="mt-1.5 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide"
            style={{ background: roleColor.bg, color: roleColor.fg }}
          >
            {ROLE_LABEL[user.role] || user.role}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/change-password" data-testid="menu-change-password">
            <KeyRound className="h-3.5 w-3.5 mr-2" /> Cambia password
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings/email" data-testid="menu-email-settings">
            <Settings className="h-3.5 w-3.5 mr-2" /> Impostazioni email
          </Link>
        </DropdownMenuItem>
        {hasRole(user, "titolare", "super_admin") && (
          <DropdownMenuItem asChild>
            <Link to="/reports/log-offerte" data-testid="menu-reports">
              <TrendingUp className="h-3.5 w-3.5 mr-2" /> Log offerte
            </Link>
          </DropdownMenuItem>
        )}
        {hasRole(user, "super_admin") && (
          <>
            <DropdownMenuItem asChild>
              <Link to="/admin/users" data-testid="menu-users">
                <Users className="h-3.5 w-3.5 mr-2" /> Gestione utenti
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/admin/trash" data-testid="menu-trash">
                <Trash2 className="h-3.5 w-3.5 mr-2" /> Cestino offerte
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/admin/system" data-testid="menu-system">
                <Settings className="h-3.5 w-3.5 mr-2" /> Sistema
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/admin" data-testid="menu-admin">
                <Settings className="h-3.5 w-3.5 mr-2" /> Pannello admin
              </Link>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={doLogout} data-testid="menu-logout">
          <LogOut className="h-3.5 w-3.5 mr-2" /> Esci
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
